import React, { useEffect, useState, useMemo } from "react";
import { createPortal } from "react-dom";
import { PageHeader, Card, Tag, fmt } from "../components/UI";
import { Gift, Plus, Trash2, X, Award, Image as ImageIcon, Trophy, ShoppingCart, AlertTriangle, Package, Calendar, RefreshCw, Upload, Pencil, Smartphone, Save } from "lucide-react";
import api, { formatError } from "../lib/api";

// Resolve a stored image reference to a renderable src.
// Uploaded images are stored as relative "/api/uploads/image/{id}" paths (domain-independent);
// legacy/external rewards may hold full http(s):// or data: URIs — return those untouched.
const imgSrc = (u) => (u && u.startsWith("/api/")) ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

export default function Rewards() {
  const [items, setItems] = useState([]);
  const [physios, setPhysios] = useState([]);
  const [slabs, setSlabs] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [tab, setTab] = useState("standard"); // standard / bonus
  const [savedFlash, setSavedFlash] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null); // reward id when editing, null when adding
  const [programFilter, setProgramFilter] = useState("");
  const [slabFilter, setSlabFilter] = useState("");
  const [includeExpired, setIncludeExpired] = useState(true);
  const [cart, setCart] = useState([]); // [{reward, qty}]
  const [checkout, setCheckout] = useState(false);
  const [selectedPhysio, setSelectedPhysio] = useState(null);
  const [activeBalance, setActiveBalance] = useState(0);
  const [uploadingPrimary, setUploadingPrimary] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  // Point 68 — which program's rewards physios see in the WhatsApp redemption menu
  const [redeemProgId, setRedeemProgId] = useState("");
  const [redeemProgs, setRedeemProgs] = useState([]);
  const [savingRedeem, setSavingRedeem] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState("");
  const [form, setForm] = useState({
    slab_ids: [],
    item_name: "",
    item_description: "",
    image_url: "",
    gallery_images: [],
    achievement_points: 5000,
    points_to_redeem: 1000,
    stock: 50,
    program_id: "",
    start_date: "",
    end_date: "",
    low_stock_threshold: 10,
    vendor: "",
    vendor_contact: "",
  });

  async function fetchAll() {
    const qs = new URLSearchParams();
    if (includeExpired) qs.set("include_expired", "true");
    const [a, b, c, d] = await Promise.all([
      api.get(`/rewards?${qs.toString()}`),
      api.get("/physios?limit=200"),
      api.get("/milestones/slabs"),
      api.get("/programs?include_inactive=true"),
    ]);
    setItems(a.data); setPhysios(b.data); setSlabs(c.data); setPrograms(d.data);
  }
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const qs = new URLSearchParams();
        if (includeExpired) qs.set("include_expired", "true");
        const [a, b, c, d] = await Promise.all([
          api.get(`/rewards?${qs.toString()}`),
          api.get("/physios?limit=200"),
          api.get("/milestones/slabs"),
          api.get("/programs?include_inactive=true"),
        ]);
        if (!alive) return;
        setItems(a.data); setPhysios(b.data); setSlabs(c.data); setPrograms(d.data);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, [includeExpired]);

  function toggleSlab(id) {
    setForm((f) => ({ ...f, slab_ids: f.slab_ids.includes(id) ? f.slab_ids.filter((x) => x !== id) : [...f.slab_ids, id] }));
  }

  // Point 68 — load + persist the physio WhatsApp redemption catalogue setting
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/settings/redemption-catalogue");
        if (!alive) return;
        setRedeemProgId(data.redemption_program_id || "");
        setRedeemProgs(data.programs || []);
      } catch { /* swallow */ }
    })();
    return () => { alive = false; };
  }, []);

  async function saveRedemptionCatalogue(pid) {
    setSavingRedeem(true); setRedeemMsg("");
    try {
      await api.put("/settings/redemption-catalogue", { redemption_program_id: pid });
      setRedeemProgId(pid);
      const prog = redeemProgs.find((p) => p.id === pid);
      setRedeemMsg(pid
        ? `Saved — physios will now see only "${prog ? prog.name : "the selected program"}" rewards when they redeem on WhatsApp.`
        : "Saved — physios will see the current quarter's rewards automatically (by date).");
      setTimeout(() => setRedeemMsg(""), 6000);
    } catch (e) { setRedeemMsg(formatError(e)); }
    finally { setSavingRedeem(false); }
  }

  const blankForm = () => ({ slab_ids: [], item_name: "", item_description: "", image_url: "", gallery_images: [], achievement_points: 5000, points_to_redeem: 1000, stock: 50, program_id: "", start_date: "", end_date: "", low_stock_threshold: 10, vendor: "", vendor_contact: "" });

  async function openAddRewardModal() {
    // Always refresh slabs + programs right before opening so newly configured
    // milestones (created on the /milestones page) show up immediately without
    // a hard page reload.
    try {
      const [c, d] = await Promise.all([api.get("/milestones/slabs"), api.get("/programs?include_inactive=true")]);
      setSlabs(c.data); setPrograms(d.data);
    } catch { /* swallow — user can hit the inline refresh button inside the modal */ }
    setEditingId(null);
    setForm(blankForm());
    setOpen(true);
  }

  async function openEditRewardModal(r) {
    // Refresh slabs + programs so the picker reflects the latest config, then
    // pre-fill the form from the existing reward and switch the modal to edit mode.
    try {
      const [c, d] = await Promise.all([api.get("/milestones/slabs"), api.get("/programs?include_inactive=true")]);
      setSlabs(c.data); setPrograms(d.data);
    } catch { /* swallow */ }
    setForm({
      slab_ids: r.slab_ids || [],
      item_name: r.item_name || r.name || "",
      item_description: r.item_description || r.description || "",
      image_url: r.image_url || (r.gallery_images && r.gallery_images[0]) || "",
      gallery_images: r.gallery_images || (r.image_url ? [r.image_url] : []),
      achievement_points: r.achievement_points ?? 0,
      points_to_redeem: r.points_to_redeem ?? r.points ?? 0,
      stock: r.stock_total ?? r.stock ?? 0,
      program_id: r.program_id || "",
      start_date: r.start_date || "",
      end_date: r.end_date || "",
      low_stock_threshold: r.low_stock_threshold ?? 10,
      vendor: (r.vendor && r.vendor !== "Slab-linked") ? r.vendor : "",
      vendor_contact: r.vendor_contact || "",
    });
    setEditingId(r.id);
    setOpen(true);
  }

  function closeModal() {
    setOpen(false);
    setEditingId(null);
    setForm(blankForm());
  }


  async function refreshSlabs() {
    try {
      const { data } = await api.get("/milestones/slabs");
      setSlabs(data);
    } catch (e) { alert(formatError(e)); }
  }

  // Map a program to the slab "window" key its milestones use (Q1-FY26 → q1, ANNUAL-FY26 → annual).
  function programWindow(p) {
    if (!p) return null;
    if (p.kind === "annual" || /annual/i.test(p.code || "")) return "annual";
    const mq = (p.code || "").match(/Q(\d)/i);
    return mq ? `q${mq[1]}` : null;
  }

  // The milestone slabs that belong to the currently-selected Program.
  // A slab belongs to a program if it carries the matching program_id/program_code,
  // OR (for legacy slabs without a program link) its window matches the program's window.
  const programSlabs = useMemo(() => {
    if (!programFilter) return slabs;
    const prog = programs.find((p) => p.id === programFilter);
    if (!prog) return slabs;
    const win = programWindow(prog);
    return slabs.filter((s) =>
      s.program_id === prog.id ||
      s.program_code === prog.code ||
      (!s.program_id && !s.program_code && win && s.window === win) ||
      (win && s.window === win)
    );
  }, [slabs, programs, programFilter]);

  const programSlabIds = useMemo(() => new Set(programSlabs.map((s) => s.id)), [programSlabs]);

  // Human-readable window label for a slab (Point 67 — never hardcode "Qtr 1").
  const winLabel = (w) => (w === "annual" ? "Annual" : (w ? String(w).toUpperCase() : "—"));

  // Point 67 — slabs shown in the Add/Edit modal, scoped to the Program picked in the form so
  // "selecting Qtr 2" only offers Q2 slabs (no cross-quarter mix-ups). No program → all slabs.
  const modalSlabs = useMemo(() => {
    if (!form.program_id) return slabs;
    const prog = programs.find((p) => p.id === form.program_id);
    if (!prog) return slabs;
    const win = programWindow(prog);
    return slabs.filter((s) =>
      s.program_id === prog.id ||
      s.program_code === prog.code ||
      (win && s.window === win)
    );
  }, [slabs, programs, form.program_id]);

  // When the Program changes, drop a milestone selection that no longer belongs to it.
  useEffect(() => {
    if (slabFilter && !programSlabIds.has(slabFilter)) setSlabFilter("");
  }, [programFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  // Apply tab + Program (superset) + milestone slab filters client-side.
  const visibleItems = useMemo(() => items.filter((r) => {
    const isBonus = r.tier === "bonus" || (r.slab_ids && r.slab_ids.length) || (r.slab_labels && r.slab_labels.length);
    if (tab === "bonus" && !isBonus) return false;
    if (tab === "standard" && isBonus) return false;
    // Program is a SUPERSET of its milestones: a reward belongs to the selected program
    // if it is tagged to that program directly OR linked to any slab inside that program.
    if (programFilter) {
      const linked = r.slab_ids || [];
      const inProgram = r.program_id === programFilter || linked.some((id) => programSlabIds.has(id));
      if (!inProgram) return false;
    }
    if (slabFilter) {
      const linkedSlabIds = r.slab_ids || [];
      if (!linkedSlabIds.includes(slabFilter)) return false;
    }
    return true;
  }), [items, tab, slabFilter, programFilter, programSlabIds]);

  // Defensive de-dup: collapse any duplicate programs by `code` (keeps first seen) so the
  // dropdowns never render the same program twice even if the API returns dirty data.
  const uniquePrograms = useMemo(() => {
    const seen = new Set();
    return programs.filter((p) => {
      const key = p.code || p.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [programs]);

  function validImage(file) {
    if (!/^image\/(jpeg|jpg|png|webp|gif)$/i.test(file.type)) { alert(`"${file.name}" is not a supported image. Please choose a JPG, PNG, WEBP or GIF.`); return false; }
    if (file.size > 25 * 1024 * 1024) { alert(`"${file.name}" is too large (max 25 MB).`); return false; }
    return true;
  }

  // Resize/compress on the client so large phone photos upload reliably and stay
  // well under the 5 MB server cap. Animated GIFs are passed through untouched.
  async function compressImage(file) {
    if (file.type === "image/gif") return file;
    try {
      const dataUrl = await new Promise((res, rej) => {
        const fr = new FileReader();
        fr.onload = () => res(fr.result);
        fr.onerror = rej;
        fr.readAsDataURL(file);
      });
      const img = await new Promise((res, rej) => {
        const im = new Image();
        im.onload = () => res(im);
        im.onerror = rej;
        im.src = dataUrl;
      });
      const MAX = 1600;
      let { width, height } = img;
      if (width > MAX || height > MAX) {
        const scale = Math.min(MAX / width, MAX / height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      const isPng = file.type === "image/png";
      const mime = isPng ? "image/png" : "image/jpeg";
      const blob = await new Promise((res) => canvas.toBlob(res, mime, 0.85));
      if (!blob || blob.size >= file.size) return file; // keep original if no gain
      const ext = isPng ? "png" : "jpg";
      const base = (file.name || "image").replace(/\.[^.]+$/, "");
      return new File([blob], `${base}.${ext}`, { type: mime });
    } catch {
      return file; // fall back to original on any failure
    }
  }

  async function uploadOne(file) {
    const out = await compressImage(file);
    const fd = new FormData();
    fd.append("file", out);
    const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
    return data.url;
  }

  async function handlePrimaryFile(e) {
    const file = e.target.files && e.target.files[0];
    e.target.value = ""; // allow re-picking the same file
    if (!file || !validImage(file)) return;
    setUploadingPrimary(true);
    try {
      const url = await uploadOne(file);
      setForm((f) => ({ ...f, image_url: url }));
    } catch (err) { alert(formatError(err)); }
    finally { setUploadingPrimary(false); }
  }

  async function handleGalleryFiles(e) {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    setUploadingGallery(true);
    try {
      for (const file of files) {
        if (!validImage(file)) continue;
        const url = await uploadOne(file);
        setForm((f) => ({ ...f, gallery_images: [...f.gallery_images, url] }));
      }
    } catch (err) { alert(formatError(err)); }
    finally { setUploadingGallery(false); }
  }

  async function submit(e) {
    e.preventDefault();
    if (!form.item_name.trim()) { alert("Item Name is required."); return; }
    try {
      const payload = { ...form };
      if (!payload.gallery_images.length && payload.image_url) payload.gallery_images = [payload.image_url];

      if (editingId) {
        // ---- Edit mode: PUT to update the existing reward ----
        const { data: updated } = await api.put(`/rewards/v2/${editingId}`, payload);
        const willBeBonus = (payload.slab_ids && payload.slab_ids.length > 0) || updated?.tier === "bonus";
        const tagged = { ...updated, __justCreated: true };
        setItems((prev) => prev.map((r) => (r.id === editingId ? { ...r, ...tagged } : r)));
        setSlabFilter("");
        setProgramFilter("");
        setTab(willBeBonus ? "bonus" : "standard");
        closeModal();
        setSavedFlash({ name: updated?.item_name || updated?.name || payload.item_name, tab: willBeBonus ? "Bonus Catalog (Slab-linked)" : "Reward Catalog (Standard)", id: updated?.id, edited: true });
        setTimeout(() => setSavedFlash(null), 6000);
        (async () => {
          try {
            const rqs = new URLSearchParams();
            if (includeExpired) rqs.set("include_expired", "true");
            const { data } = await api.get(`/rewards?${rqs.toString()}`);
            setItems(data.some((r) => r.id === editingId) ? data : [...data, tagged]);
          } catch { /* keep optimistic state */ }
        })();
        setTimeout(() => {
          const node = document.querySelector(`[data-testid="reward-card-${updated?.id}"]`);
          if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
        }, 100);
        return;
      }

      const { data: newReward } = await api.post("/rewards/v2", payload);
      const willBeBonus = (payload.slab_ids && payload.slab_ids.length > 0) || newReward?.tier === "bonus";
      // ---- Optimistic update — prepend the new reward locally so it shows up instantly,
      //      bypassing any current filter (the new reward gets a __justCreated flag for the highlight).
      const tagged = { ...newReward, __justCreated: true };
      setItems((prev) => [tagged, ...prev.filter((r) => r.id !== tagged.id)]);
      // Reset filters + switch to the correct tab so it's definitely visible
      setSlabFilter("");
      setProgramFilter("");
      setTab(willBeBonus ? "bonus" : "standard");
      closeModal();
      // Toast banner — auto-dismisses in 6s
      const tabLabel = willBeBonus ? "Bonus Catalog (Slab-linked)" : "Reward Catalog (Standard)";
      setSavedFlash({ name: newReward?.item_name || newReward?.name || payload.item_name, tab: tabLabel, id: newReward?.id });
      setTimeout(() => setSavedFlash(null), 6000);
      // Background reconcile with server — but GUARANTEE the new reward stays visible even
      // if the list refetch races, is capped, or omits it (Point 12 — a just-added reward
      // must NEVER disappear, incl. quarter/slab-linked rewards).
      (async () => {
        try {
          const rqs = new URLSearchParams();
          if (includeExpired) rqs.set("include_expired", "true");
          const { data } = await api.get(`/rewards?${rqs.toString()}`);
          setItems(data.some((r) => r.id === newReward.id) ? data : [tagged, ...data]);
        } catch { /* keep optimistic state — reward already shown */ }
      })();
      // Scroll the grid into view + briefly highlight the new card
      setTimeout(() => {
        const node = document.querySelector(`[data-testid="reward-card-${newReward?.id}"]`);
        if (node) node.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 100);
    } catch (e) { alert(formatError(e)); }
  }

  async function del(id) {
    if (!window.confirm("Delete reward?")) return;
    await api.delete(`/rewards/${id}`); fetchAll();
  }

  function addToCart(reward) {
    setCart((c) => {
      const existing = c.find((x) => x.reward.id === reward.id);
      if (existing) return c.map((x) => x.reward.id === reward.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { reward, qty: 1 }];
    });
  }

  function removeCartItem(id) { setCart((c) => c.filter((x) => x.reward.id !== id)); }

  function cartTotal() { return cart.reduce((sum, x) => sum + (x.reward.points_to_redeem || x.reward.points || 0) * x.qty, 0); }

  async function openCheckout() {
    if (cart.length === 0) return;
    setCheckout(true);
  }

  async function pickPhysio(p) {
    setSelectedPhysio(p);
    try {
      const { data } = await api.get(`/physios/${p.id}`);
      setActiveBalance(data?.wallet?.active || 0);
    } catch { setActiveBalance(0); }
  }

  async function confirmCart() {
    if (!selectedPhysio) { alert("Select a physio."); return; }
    try {
      const payload = { physio_id: selectedPhysio.id, items: cart.map((x) => ({ reward_id: x.reward.id, qty: x.qty })) };
      const { data } = await api.post("/redemptions/cart", payload);
      alert(`Reserved ${data.redemptions.length} reward(s) totalling ${data.total_points} pts. Awaiting dispatch.`);
      setCart([]); setCheckout(false); setSelectedPhysio(null); fetchAll();
    } catch (e) { alert(formatError(e)); }
  }

  const filteredPhysios = useMemo(() => physios.filter((p) => activeBalance ? true : true), [physios, activeBalance]);

  return (
    <div className="space-y-6">
      {savedFlash && (
        <div className="surface p-3 border-l-4 border-emerald-500 bg-emerald-50/60 flex items-start gap-2" data-testid="reward-saved-flash">
          <Trophy size={16} className="text-emerald-700 mt-0.5 shrink-0" />
          <div className="flex-1 text-[12.5px] text-emerald-900">
            <strong>{savedFlash.edited ? "Reward updated" : "Reward added to catalog"}</strong> · <span className="font-medium">{savedFlash.name}</span>
            <span className="text-emerald-700"> — visible in the <em>{savedFlash.tab}</em> tab below.</span>
          </div>
          <button onClick={() => setSavedFlash(null)} className="text-emerald-600 hover:text-emerald-800"><X size={14} /></button>
        </div>
      )}
      <PageHeader
        kicker="Engagement"
        title="Reward Catalog"
        subtitle="Points-milestone rewards organised by Quarterly / Annual programs. Cart-style multi-redeem with point reservation guardrails — physios can redeem multiple rewards in one go."
        testId="rewards-page"
        actions={
          <div className="flex items-center gap-2">
            {cart.length > 0 && (
              <button onClick={openCheckout} className="btn-teal inline-flex items-center gap-2" data-testid="open-cart-btn">
                <ShoppingCart size={14} /> Cart · {cart.length} · {fmt(cartTotal())} pts
              </button>
            )}
            <button onClick={openAddRewardModal} className="btn-primary inline-flex items-center gap-2" data-testid="add-reward-btn"><Plus size={14} /> Add Reward</button>
          </div>
        }
      />

      {/* Point 68 — admin control: pick which program's rewards physios can redeem on WhatsApp */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3" data-testid="redemption-catalogue-setting">
          <div className="flex items-start gap-2.5 flex-1">
            <div className="h-9 w-9 rounded-lg bg-teal-50 text-teal-700 grid place-items-center shrink-0"><Smartphone size={16} /></div>
            <div>
              <div className="font-medium text-slate-900 text-[13.5px]">Physio WhatsApp Redemption Catalogue</div>
              <div className="text-[11.5px] text-slate-500 leading-snug">Physios see <strong>only</strong> this program's rewards when they redeem on WhatsApp. Annual rewards are hidden unless Annual is selected here.</div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <select value={redeemProgId} onChange={(e) => saveRedemptionCatalogue(e.target.value)} disabled={savingRedeem}
              className="!py-1.5 !w-auto min-w-[220px]" data-testid="redemption-program-select">
              <option value="">Auto — current quarter by date</option>
              {redeemProgs.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.code}`}</option>)}
            </select>
            {savingRedeem ? <RefreshCw size={15} className="animate-spin text-slate-400" /> : <Save size={15} className="text-slate-300" />}
          </div>
        </div>
        {redeemMsg && <div className="mt-2 text-[12px] text-teal-800 bg-teal-50 border border-teal-200 rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5" data-testid="redemption-saved-msg"><Trophy size={13} /> {redeemMsg}</div>}
      </Card>

      <Card>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          {/* Two-tab Standard / Bonus */}
          <div className="inline-flex items-center bg-slate-100 rounded-full p-0.5 text-[12px]" data-testid="reward-tabs">
            {[
              ["standard", "Reward Catalog (Standard)"],
              ["bonus", "Bonus Catalog (Slab-linked)"],
            ].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`reward-tab-${k}`}
                className={`px-3 py-1 rounded-full transition ${tab === k ? "bg-white text-slate-900 shadow-sm font-medium" : "text-slate-500 hover:text-slate-900"}`}>{l}</button>
            ))}
          </div>
          <label className="small-caps text-slate-500 ml-3">Milestone</label>
          <select value={slabFilter} onChange={(e) => setSlabFilter(e.target.value)} className="!py-1.5 !w-auto" data-testid="milestone-filter">
            <option value="">{programSlabs.length === 0 ? (programFilter ? "No milestones in this program" : "No milestones configured — see Milestones page") : (programFilter ? "All milestones in this program" : "All milestones")}</option>
            {programSlabs.map((s) => <option key={s.id} value={s.id}>{`${s.name} · ${s.window === "annual" ? "Annual" : (s.window || "").toUpperCase()} · ${s.threshold_points}p`}</option>)}
          </select>
          <label className="small-caps text-slate-500 ml-3">Program</label>
          <select value={programFilter} onChange={(e) => setProgramFilter(e.target.value)} className="!py-1.5 !w-auto" data-testid="program-filter">
            <option value="">All programs</option>
            {uniquePrograms.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.code}`}</option>)}
          </select>
          <label className="text-[12px] inline-flex items-center gap-1.5 cursor-pointer">
            <input type="checkbox" checked={includeExpired} onChange={(e) => setIncludeExpired(e.target.checked)} data-testid="include-expired" />
            Show expired
          </label>
          <div className="text-[11px] text-slate-400 ml-auto">{visibleItems.length} reward(s) in this tab</div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visibleItems.length === 0 && items.length > 0 && (
            <div className="col-span-full surface p-6 text-center text-slate-500 text-[12.5px]" data-testid="rewards-empty-other-tab">
              No rewards in the <strong>{tab === "bonus" ? "Bonus (Slab-linked)" : "Standard"}</strong> tab match the current filters.
              {items.some((r) => (tab === "bonus" ? !(r.tier === "bonus" || (r.slab_ids?.length)) : (r.tier === "bonus" || (r.slab_ids?.length)))) && (
                <button onClick={() => setTab(tab === "bonus" ? "standard" : "bonus")} className="ml-1 text-teal-700 hover:underline">
                  Switch to the {tab === "bonus" ? "Standard" : "Bonus"} tab →
                </button>
              )}
            </div>
          )}
          {visibleItems.length === 0 && items.length === 0 && (
            <div className="col-span-full surface p-8 text-center text-slate-500 text-[12.5px]" data-testid="rewards-empty-catalog">
              <Gift size={28} className="mx-auto text-slate-400 mb-2" />
              No rewards in the catalog yet. Click <strong>Add Reward</strong> to create your first one.
            </div>
          )}
          {visibleItems.map((r) => {
            const inv = r.inventory || {};
            const gallery = (r.gallery_images && r.gallery_images.length) ? r.gallery_images : (r.image_url ? [r.image_url] : []);
            const program = programs.find((p) => p.id === r.program_id);
            return (
              <div key={r.id} className={`surface p-0 surface-hover flex flex-col overflow-hidden ${r.__justCreated ? "ring-2 ring-emerald-400 ring-offset-2" : ""}`} data-testid={`reward-card-${r.id}`}>
                {gallery[0] ? (
                  <div className="relative">
                    <img src={imgSrc(gallery[0])} alt={r.name} className="h-32 w-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    {gallery.length > 1 && <div className="absolute bottom-1 right-1 bg-slate-900/70 text-white text-[10px] px-1.5 py-0.5 rounded">+{gallery.length - 1} more</div>}
                  </div>
                ) : (
                  <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 grid place-items-center"><Gift size={32} className="text-slate-400" /></div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-slate-900 leading-snug line-clamp-2">{r.name || r.item_name}</div>
                    <div className="flex items-center gap-1.5 shrink-0">
                      <button onClick={() => openEditRewardModal(r)} className="text-slate-400 hover:text-brand-navy" title="Edit reward" data-testid={`edit-reward-${r.id}`}><Pencil size={13} /></button>
                      <button onClick={() => del(r.id)} className="text-slate-400 hover:text-rose-600" title="Delete reward" data-testid={`delete-reward-${r.id}`}><Trash2 size={13} /></button>
                    </div>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{r.description || r.item_description}</div>
                  {(r.vendor || r.vendor_contact) && (
                    <div className="text-[10.5px] text-slate-500 mt-1.5 flex flex-wrap gap-x-2" data-testid={`reward-vendor-${r.id}`}>
                      {r.vendor && r.vendor !== "Slab-linked" && <span><span className="small-caps text-slate-400">Vendor:</span> {r.vendor}</span>}
                      {r.vendor_contact && <span className="text-slate-400">· {r.vendor_contact}</span>}
                    </div>
                  )}
                  <div className="flex flex-wrap gap-1 mt-2">
                    {program && <Tag tone="slate"><Calendar size={10} /> {program.code}</Tag>}
                    {(r.slab_labels || []).map((sl, i) => <Tag key={i} tone="teal"><Trophy size={10} /> {sl}</Tag>)}
                    {(!r.slab_labels || r.slab_labels.length === 0) && !program && <Tag tone="slate">{r.tier === "bonus" ? "Bonus" : "Standard"}</Tag>}
                    {r.expired && <Tag tone="rose">Expired</Tag>}
                    {r.is_certificate && <Tag tone="emerald">Certificate</Tag>}
                  </div>
                  <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-[11px]">
                    <div><div className="small-caps text-slate-400">Achievement</div><div className="text-slate-900 numerals font-medium">{fmt(r.achievement_points || 0)} pts</div></div>
                    <div><div className="small-caps text-slate-400">To Redeem</div><div className="text-slate-900 numerals font-medium">{fmt(r.points_to_redeem || r.points || 0)} pts</div></div>
                  </div>
                  {/* Inventory state pills */}
                  <div className="mt-2 grid grid-cols-4 gap-1 text-center">
                    <div title="Available" className={`text-[10px] py-0.5 rounded ${inv.low_stock_alert ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
                      <div className="numerals font-semibold">{inv.available ?? 0}</div><div className="small-caps">Avail</div>
                    </div>
                    <div title="Reserved" className="text-[10px] py-0.5 rounded bg-amber-50 text-amber-700">
                      <div className="numerals font-semibold">{inv.reserved ?? 0}</div><div className="small-caps">Resv</div>
                    </div>
                    <div title="Dispatched" className="text-[10px] py-0.5 rounded bg-teal-50 text-teal-700">
                      <div className="numerals font-semibold">{inv.dispatched ?? 0}</div><div className="small-caps">Disp</div>
                    </div>
                    <div title="Delivered" className="text-[10px] py-0.5 rounded bg-slate-100 text-slate-600">
                      <div className="numerals font-semibold">{inv.delivered ?? 0}</div><div className="small-caps">Delv</div>
                    </div>
                  </div>
                  {inv.low_stock_alert && (
                    <div className="mt-2 text-[10.5px] text-rose-700 flex items-center gap-1"><AlertTriangle size={11} /> Low stock — re-procurement needed</div>
                  )}
                  <div className="mt-auto pt-3 flex items-center justify-between gap-1">
                    <div className="text-[11px] text-slate-500">Total <span className="numerals">{inv.total ?? r.stock}</span></div>
                    <button onClick={() => addToCart(r)} disabled={r.expired || (inv.available ?? 0) === 0}
                      className={`!py-1 !px-3 text-[12px] rounded-md inline-flex items-center gap-1 ${(r.expired || (inv.available ?? 0) === 0) ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-brand-navy text-white hover:opacity-90"}`}
                      data-testid={`add-cart-${r.id}`}>
                      <ShoppingCart size={11} /> Add
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
          {items.length === 0 && <div className="col-span-full text-center py-10 text-slate-400 text-[13px]">No rewards yet. Click <b className="text-slate-700">Add Reward</b> to seed your first milestone reward.</div>}
        </div>
      </Card>

      {/* Add/Edit reward modal */}
      {open && createPortal(
        <div className="fixed inset-0 bg-slate-900/30 z-40 flex items-center justify-center p-4" onClick={closeModal}>
          <form onSubmit={submit} className="bg-white rounded-xl border border-slate-200 w-full max-w-2xl p-6 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-reward-form">
            <div className="flex items-center justify-between">
              <h3 className="serif-display text-2xl">{editingId ? "Edit Reward" : "Add Reward"}</h3>
              <button type="button" onClick={closeModal}><X size={18} /></button>
            </div>

            <div>
              <label className="small-caps text-slate-500">Program (Quarterly / Annual)</label>
              <select value={form.program_id} onChange={(e) => setForm({ ...form, program_id: e.target.value })} data-testid="program-select">
                <option value="">— Select program —</option>
                {uniquePrograms.map((p) => <option key={p.id} value={p.id}>{`${p.name} · ${p.code} (${p.start_date} → ${p.end_date})`}</option>)}
              </select>
              {(() => {
                const sel = programs.find((p) => p.id === form.program_id);
                const today = new Date().toISOString().slice(0, 10);
                if (!sel || !sel.end_date || String(sel.end_date).slice(0, 10) >= today) return null;
                return (
                  <div className="mt-1.5 text-[11px] text-amber-700 flex items-start gap-1.5" data-testid="program-ended-note">
                    <AlertTriangle size={11} className="shrink-0 mt-0.5" />
                    <span>{sel.name} ended on {String(sel.end_date).slice(0, 10)}. Rewards are still saved and listed here (tagged Expired). Physios can redeem them on WhatsApp when this program is selected as the Redemption Catalogue above.</span>
                  </div>
                );
              })()}
            </div>

            <div>
              <label className="small-caps text-slate-500 flex items-center gap-1">
                <Award size={11} /> Select Slabs (from Milestone Engine)
                <button type="button" onClick={refreshSlabs} className="ml-2 text-[10px] text-teal-700 inline-flex items-center gap-0.5 hover:underline" data-testid="refresh-slabs-btn" title="Re-fetch slabs from Milestone Engine">
                  <RefreshCw size={10} /> Refresh
                </button>
                <span className="ml-auto text-[10px] text-slate-400">{slabs.length} configured</span>
              </label>
              <div className="mt-1.5 flex flex-wrap gap-1.5 max-h-32 overflow-y-auto border border-slate-200 rounded-md p-2" data-testid="slab-picker">
                {modalSlabs.length === 0 && (
                  <div className="text-[11px] text-amber-700 px-1 flex items-center gap-1.5 w-full" data-testid="no-slabs-warning">
                    <AlertTriangle size={11} className="shrink-0" />
                    <span>No milestone slabs {form.program_id ? "for this program" : "configured"} yet. Open the <a href="/milestones" className="underline font-medium">Milestone Engine</a> to add {form.program_id ? "its" : "Qtr 1 / Annual"} slabs first, then click <em>Refresh</em>.</span>
                  </div>
                )}
                {modalSlabs.map((s) => (
                  <button key={s.id} type="button" onClick={() => toggleSlab(s.id)}
                    data-testid={`slab-pick-${s.id}`}
                    className={`text-[11px] px-2 py-1 rounded-full border transition ${form.slab_ids.includes(s.id) ? "bg-brand-navy text-white border-brand-navy" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
                    title={`${s.name} · ${s.window === "annual" ? "Annual Program" : winLabel(s.window) + " Program"} · ${s.threshold_points} pts · ${s.reward_label || ""}`}>
                    {s.name} <span className="opacity-60">({winLabel(s.window)} · {s.threshold_points}p)</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="small-caps text-slate-500">Item Name</label>
              <input type="text" value={form.item_name} onChange={(e) => setForm({ ...form, item_name: e.target.value })} maxLength={300} required className="w-full" data-testid="item-name" />
            </div>

            <div>
              <label className="small-caps text-slate-500">Item Description</label>
              <textarea value={form.item_description} onChange={(e) => setForm({ ...form, item_description: e.target.value })} maxLength={3000} rows={3} className="w-full" data-testid="item-description" />
              <div className="text-[10px] text-slate-400 text-right">{form.item_description.length}/3000</div>
            </div>

            <div>
              <label className="small-caps text-slate-500 flex items-center gap-1"><ImageIcon size={11} /> Primary Image</label>
              <div className="mt-1.5 flex items-center gap-3">
                {form.image_url ? (
                  <div className="relative group shrink-0">
                    <img src={imgSrc(form.image_url)} alt="preview" className="h-24 w-24 object-cover rounded-md border border-slate-200" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, image_url: "" }))} className="absolute -top-1.5 -right-1.5 bg-rose-600 text-white rounded-full p-0.5" data-testid="remove-primary-image" title="Remove image"><X size={11} /></button>
                  </div>
                ) : (
                  <label className={`h-24 w-24 shrink-0 border-2 border-dashed border-slate-300 rounded-md text-slate-400 hover:border-teal-700 hover:text-teal-700 inline-flex flex-col items-center justify-center cursor-pointer text-[11px] gap-1 transition ${uploadingPrimary ? "opacity-60 pointer-events-none" : ""}`} data-testid="primary-image-dropzone">
                    {uploadingPrimary ? <RefreshCw size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span>{uploadingPrimary ? "Uploading…" : "Browse"}</span>
                    <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" className="hidden" onChange={handlePrimaryFile} data-testid="primary-image-input" />
                  </label>
                )}
                <div className="text-[10.5px] text-slate-400 leading-relaxed">
                  Attach an image from your device.<br />JPG, PNG, WEBP or GIF · large photos are auto-optimised.
                </div>
              </div>
            </div>

            <div>
              <label className="small-caps text-slate-500 flex items-center gap-1"><ImageIcon size={11} /> Listing Gallery (additional benefit / spec images)</label>
              <div className="mt-1 flex flex-wrap gap-2">
                {form.gallery_images.map((u, i) => (
                  <div key={i} className="relative group">
                    <img src={imgSrc(u)} alt={`g${i}`} className="h-14 w-14 object-cover rounded border" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                    <button type="button" onClick={() => setForm((f) => ({ ...f, gallery_images: f.gallery_images.filter((_, j) => j !== i) }))} className="absolute -top-1 -right-1 bg-rose-600 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100"><X size={10} /></button>
                  </div>
                ))}
                <label className={`h-14 w-14 border-2 border-dashed border-slate-300 rounded text-slate-400 hover:border-teal-700 hover:text-teal-700 inline-flex items-center justify-center cursor-pointer transition ${uploadingGallery ? "opacity-60 pointer-events-none" : ""}`} data-testid="add-gallery-img" title="Attach gallery images">
                  {uploadingGallery ? <RefreshCw size={14} className="animate-spin" /> : <Plus size={16} />}
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp,image/gif" multiple className="hidden" onChange={handleGalleryFiles} data-testid="gallery-image-input" />
                </label>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="small-caps text-slate-500">Start Date</label>
                <input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="start-date" />
              </div>
              <div>
                <label className="small-caps text-slate-500">End Date</label>
                <input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="end-date" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
              <div>
                <label className="small-caps text-slate-500">Achievement Pts</label>
                <input type="number" min="0" value={form.achievement_points} onChange={(e) => setForm({ ...form, achievement_points: Number(e.target.value) })} data-testid="achievement-points" />
              </div>
              <div>
                <label className="small-caps text-slate-500">Redeem Pts</label>
                <input type="number" min="0" value={form.points_to_redeem} onChange={(e) => setForm({ ...form, points_to_redeem: Number(e.target.value) })} data-testid="redeem-points" />
              </div>
              <div>
                <label className="small-caps text-slate-500">Total Stock</label>
                <input type="number" min="0" value={form.stock} onChange={(e) => setForm({ ...form, stock: Number(e.target.value) })} data-testid="stock" />
              </div>
              <div>
                <label className="small-caps text-slate-500">Low Stock Alert</label>
                <input type="number" min="0" value={form.low_stock_threshold} onChange={(e) => setForm({ ...form, low_stock_threshold: Number(e.target.value) })} data-testid="low-stock-threshold" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="small-caps text-slate-500">Vendor</label>
                <input type="text" value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })} placeholder="e.g. Amazon Pay Gifts, Tanishq" className="w-full" data-testid="vendor" maxLength={200} />
              </div>
              <div>
                <label className="small-caps text-slate-500">Vendor Contact</label>
                <input type="text" value={form.vendor_contact} onChange={(e) => setForm({ ...form, vendor_contact: e.target.value })} placeholder="POC name · email · phone" className="w-full" data-testid="vendor-contact" maxLength={200} />
              </div>
            </div>

            <button type="submit" className="btn-primary w-full" data-testid="submit-reward">{editingId ? "Save Changes" : "Add Reward to Catalog"}</button>
          </form>
        </div>,
        document.body
      )}

      {/* Checkout / Cart drawer */}
      {checkout && createPortal(
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4" onClick={() => setCheckout(false)}>
          <div className="bg-white rounded-xl border border-slate-200 w-full max-w-2xl p-6 space-y-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="checkout-modal">
            <div className="flex items-center justify-between">
              <h3 className="serif-display text-2xl">Redemption Cart</h3>
              <button onClick={() => setCheckout(false)}><X size={18} /></button>
            </div>

            <div className="space-y-2">
              {cart.map((x) => (
                <div key={x.reward.id} className="flex items-center justify-between gap-3 surface p-3" data-testid={`cart-item-${x.reward.id}`}>
                  <div className="flex-1">
                    <div className="font-medium text-[13px]">{x.reward.name || x.reward.item_name}</div>
                    <div className="text-[11px] text-slate-500">{fmt(x.reward.points_to_redeem || x.reward.points || 0)} pts × {x.qty}</div>
                  </div>
                  <input type="number" min="1" value={x.qty} onChange={(e) => setCart((c) => c.map((y) => y.reward.id === x.reward.id ? { ...y, qty: Number(e.target.value) || 1 } : y))} className="!w-16 !py-1" />
                  <div className="text-[13px] font-medium numerals w-20 text-right">{fmt((x.reward.points_to_redeem || x.reward.points || 0) * x.qty)}</div>
                  <button onClick={() => removeCartItem(x.reward.id)} className="text-rose-600"><Trash2 size={13} /></button>
                </div>
              ))}
              {cart.length === 0 && <div className="text-slate-400 text-center text-[12px] py-4">Cart is empty.</div>}
            </div>

            <div className="surface p-3 flex items-center justify-between border-t-2 border-teal-700">
              <div className="text-[13px] text-slate-600">Cart Total</div>
              <div className="serif-display text-2xl numerals" data-testid="cart-total-pts">{fmt(cartTotal())} pts</div>
            </div>

            {!selectedPhysio ? (
              <div>
                <label className="small-caps text-slate-500">Select Physio</label>
                <div className="max-h-72 overflow-y-auto space-y-1 mt-1">
                  {filteredPhysios.map((p) => (
                    <button key={p.id} onClick={() => pickPhysio(p)} className="w-full text-left px-3 py-2 rounded-md border border-slate-200 hover:bg-slate-50 text-[13px]" data-testid={`pick-physio-${p.id}`}>
                      <span className="font-medium">{p.first_name} {p.last_name}</span>
                      <span className="text-slate-500 text-[11px] ml-2 font-mono">{p.erp_outlet_id || "—"}</span>
                      <span className="text-slate-400 text-[11px] ml-2">{p.city}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="surface p-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium text-[13px]">{selectedPhysio.first_name} {selectedPhysio.last_name}</div>
                    <div className="text-[11px] text-slate-500 font-mono">{selectedPhysio.erp_outlet_id || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="small-caps text-slate-400">Active balance</div>
                    <div className="serif-display text-xl numerals" data-testid="checkout-active-balance">{fmt(activeBalance)}</div>
                  </div>
                  <button onClick={() => { setSelectedPhysio(null); setActiveBalance(0); }} className="text-slate-400 hover:text-slate-900 ml-3"><X size={14} /></button>
                </div>
                <div className="mt-2 text-[11.5px]">
                  After redemption: <span className="numerals font-medium">{fmt(activeBalance - cartTotal())}</span> active +
                  <span className="numerals font-medium text-amber-700"> {fmt(cartTotal())} reserved</span>
                </div>
                {activeBalance < cartTotal() && <div className="text-[11px] text-rose-700 mt-1 flex items-center gap-1"><AlertTriangle size={11} /> Insufficient active points. Reduce cart or top up.</div>}
              </div>
            )}

            <div className="flex items-center gap-2">
              <button onClick={() => { setCheckout(false); }} className="flex-1 px-3 py-2 rounded-md border border-slate-200 text-[13px]">Cancel</button>
              <button onClick={confirmCart} disabled={!selectedPhysio || cart.length === 0 || activeBalance < cartTotal()}
                className={`flex-1 px-3 py-2 rounded-md text-[13px] inline-flex items-center justify-center gap-2 ${(!selectedPhysio || activeBalance < cartTotal()) ? "bg-slate-100 text-slate-400 cursor-not-allowed" : "bg-brand-navy text-white"}`}
                data-testid="confirm-cart-btn">
                <Package size={13} /> Reserve & Checkout
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
